import React, { Component } from 'react';
import { Collapse, Navbar, NavbarBrand, NavbarToggler, NavItem, NavLink, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from '../shim/reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import './NavMenu.scss';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

const BellIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

// User info component (functional to use hooks)
const UserInfo = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      // Wait 1 second to ensure logout request completes
      await new Promise(resolve => setTimeout(resolve, 1000));
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <NavItem>
          <NavLink tag={Link} className="text-dark" to="/login">Login</NavLink>
        </NavItem>
        <NavItem>
          <NavLink tag={Link} className="text-dark" to="/signup">Sign Up</NavLink>
        </NavItem>
      </>
    );
  }

  return (
    <>
      <NavItem className="d-flex align-items-center me-1">
        <button
          type="button"
          className="btn btn-link text-dark p-1 position-relative nav-bell-btn"
          onClick={() => navigate('/notifications')}
          title="Notifications"
          style={{ lineHeight: 1 }}
        >
          <BellIcon />
          {unreadCount > 0 && (
            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
              style={{ fontSize: '0.6rem' }}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </NavItem>
      <UncontrolledDropdown nav inNavbar>
        <DropdownToggle nav caret className="text-dark nav-user-toggle" title={user?.Name || user?.Email || 'User'}>
          {user?.Name || user?.Email || 'User'}
        </DropdownToggle>
        <DropdownMenu end>
        <DropdownItem header>
          <div><strong>{user?.Name}</strong></div>
          <div className="text-muted small">{user?.Email}</div>
        </DropdownItem>
        <DropdownItem divider />
        <DropdownItem tag={Link} to="/profile">
          Profile
        </DropdownItem>
        <DropdownItem tag={Link} to="/notifications/preferences">
          Notification Preferences
        </DropdownItem>
        <DropdownItem tag={Link} to="/trading-connectors">
          Trading connectors
        </DropdownItem>
        <DropdownItem tag={Link} to="/trading-strategies">
          Trading strategies
        </DropdownItem>
        {user?.Roles?.includes('Admin') && (
          <>
            <DropdownItem tag={Link} to="/admin">
              Admin
            </DropdownItem>
          </>
        )}
        <DropdownItem divider />
        <DropdownItem tag={Link} to="/help">
          Help
        </DropdownItem>
        <DropdownItem divider />
        <DropdownItem onClick={handleLogout}>
          Logout
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
    </>
  );
};

export class NavMenu extends Component {
  static displayName = NavMenu.name;

  constructor (props) {
    super(props);

    this.toggleNavbar = this.toggleNavbar.bind(this);
    this.state = {
      collapsed: true
    };
  }

  toggleNavbar () {
    this.setState({
      collapsed: !this.state.collapsed
    });
  }

  render() {
    return (
      <header>
        <Navbar className="navbar-expand-sm navbar-toggleable-sm ng-white border-bottom box-shadow mb-3" container light>
          <NavbarBrand tag={Link} to="/">modeltrader.ai</NavbarBrand>
          <NavbarToggler onClick={this.toggleNavbar} className="mr-2" />
          <Collapse className="d-sm-inline-flex" isOpen={!this.state.collapsed} navbar>
            <ul className="navbar-nav flex-grow">
              <NavItem>
                <NavLink tag={Link} className="text-dark" to="/">Home</NavLink>
              </NavItem>
              <NavItem>
                <NavLink tag={Link} className="text-dark" to="/tickers">Tickers</NavLink>
              </NavItem>
              <NavItem>
                <NavLink tag={Link} className="text-dark" to="/candles">Candles</NavLink>
              </NavItem>
              <NavItem>
                <NavLink tag={Link} className="text-dark" to="/charts">Charts</NavLink>
              </NavItem>
              <NavItem>
                <NavLink tag={Link} className="text-dark" to="/packages">Packages</NavLink>
              </NavItem>
              <NavItem>
                <NavLink tag={Link} className="text-dark" to="/simulation-reports">Simulation Reports</NavLink>
              </NavItem>
              <NavItem>
                <NavLink tag={Link} className="text-dark" to="/dm-tuning">DM Tuning</NavLink>
              </NavItem>
              <UncontrolledDropdown nav inNavbar>
                <DropdownToggle nav caret className="text-dark">Models</DropdownToggle>
                <DropdownMenu>
                  <DropdownItem tag={Link} to="/models">Models</DropdownItem>
                  <DropdownItem tag={Link} to="/models-with-recent-signals">Models with recent signals</DropdownItem>
                  <DropdownItem tag={Link} to="/model-execution">Model Execution</DropdownItem>
                  <DropdownItem tag={Link} to="/model-spreadsheet">Model Spreadsheet</DropdownItem>
                  <DropdownItem tag={Link} to="/shared-spreadsheets">Shared Spreadsheets</DropdownItem>
                  <DropdownItem tag={Link} to="/performance-calculation">Performance Calculation</DropdownItem>
                  <DropdownItem tag={Link} to="/performance-calculation/settings">Performance Calc. Settings</DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
              <UncontrolledDropdown nav inNavbar>
                <DropdownToggle nav caret className="text-dark">Signals</DropdownToggle>
                <DropdownMenu>
                  <DropdownItem tag={Link} to="/signal-files">Signal Files</DropdownItem>
                  <DropdownItem tag={Link} to="/combine-models">Combine Models</DropdownItem>
                  <DropdownItem tag={Link} to="/signals-overlay">Signals Overlay</DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </ul>
            <ul className="navbar-nav ms-auto">
              <UserInfo />
            </ul>
          </Collapse>
        </Navbar>
      </header>
    );
  }
}
